import React, { useEffect, useState, useMemo } from "react";
import Permission from "./Permission";
import SecurityIcon from "@mui/icons-material/Security";
import RouteIcon from "@mui/icons-material/Route";
import BlockIcon from "@mui/icons-material/Block";
import baseUrl from "../../../../api/baseUrl";
import { NEWROUTES } from "../../../../utils/routes/new.routes";
import { ACCESSACTION } from "../../../../routes/accessActions";
import { useNavigate, useParams } from "react-router-dom";

// Capitalize the first letter of each word
const capitalizeTitle = (title: string): string => {
  if (!title) return title;
  return title
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

// Flatten NEWROUTES recursively to extract all routes
const flattenRoutes = (routes: any[], parentPath = ""): any[] => {
  const flattened: any[] = [];
  
  routes.forEach((route) => {
    if (route.KEY) {
      // Build full path
      const fullPath = parentPath 
        ? `${parentPath}/${route.PATH}` 
        : route.PATH;
      
      // Create route object similar to ACCESSROUTES structure
      flattened.push({
        KEY: route.KEY,
        PATH: fullPath,
        NAME: capitalizeTitle(route.TITLE),
        id: `ROUTE_${route.KEY}`, // Generate an id for consistency
      });
      
      // Recursively flatten SUBNAV if it exists
      if (route.SUBNAV && route.SUBNAV.length > 0) {
        const nestedRoutes = flattenRoutes(route.SUBNAV, fullPath);
        flattened.push(...nestedRoutes);
      }
    }
  });
  
  return flattened;
};

const actions = Object.keys(ACCESSACTION).map((key) => ({
  ...ACCESSACTION[key as keyof typeof ACCESSACTION],
  id: key, // Optionally add an id property if needed
}));

interface FormData {
  roleName: string;
  routePermission: number[];
  actionPermission: number[];
}

function AddRole() {
  const navigate = useNavigate();

  // Flatten routes inside component to avoid circular dependency
  const route = useMemo(() => flattenRoutes(NEWROUTES), []);

  const [formData, setformData] = useState<FormData>({
    roleName: "",
    routePermission: [],
    actionPermission: [],
  });
  const { ID } = useParams();
  useEffect(() => {
    if (ID) {
      baseUrl
        .get("/role/findRoleId", {
          headers: {
            roleid: ID,
          },
        })
        .then((response) => {
          setformData((prevFormData) => ({
            ...prevFormData,
            roleName: response.data.data.roleName,
            routePermission: response.data.data.routePermission,
            actionPermission: response.data.data.actionPermission,
          }));
        })
        .catch((error) => {
          console.log(error);
        });
    }
  }, [ID]);

  const handleChange = (e: any) => {
    const { name, value, checked } = e.target;

    if (name === "roleName") {
      setformData({ ...formData, [name]: value });
    } else if ((name as string).startsWith("routePermission")) {
      setformData((prevFormData) => ({
        ...prevFormData,
        routePermission: checked
          ? [...prevFormData.routePermission, value]
          : prevFormData.routePermission.filter((key) => key !== value),
      }));


    } else if ((name as string).startsWith("actionPermission")) {
      if (checked) {
        // If checkbox is checked, add the permission to the array
        setformData((prevFormData) => ({
          ...prevFormData,
          actionPermission: [...prevFormData.actionPermission, value],
        }));
      } else {
        // If checkbox is unchecked, remove the permission from the array
        setformData((prevFormData) => ({
          ...prevFormData,
          actionPermission: prevFormData.actionPermission.filter(
            (key) => key !== value
          ),
        }));
      }
    }
  };
  useEffect(() => {
    if (!ID) {
      const allPermissionKeys = route.map((permission) => permission.KEY);
      setformData((prevFormData) => ({
        ...prevFormData,
        routePermission: allPermissionKeys,
      }));
    }
  }, [ID, route]);
  const handleSubmit = (e: any) => {
    e.preventDefault();
    if (ID) {
      baseUrl
        .patch("/role/updateRole", { ...formData, roleId: ID })
        .then((response) => {
          navigate(`/client/staff/role/all-role`);
        })
        .catch((error) => {
          console.log(error);
        });
    } else {
      baseUrl
        .post("/role/addRole", formData)
        .then((response) => {
          navigate(`/client/staff/role/all-role`);
        })
        .catch((error) => {
          console.log(error);
        });
    }
  };
  function countDigits(num: number) {
    // Convert number to string and remove non-digit characters
    const numStr = Math.abs(num).toString(); // Handle negative numbers
    return numStr.replace(/\D/g, "").length; // Remove non-digit characters and count length
  }
  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="bg-white dark:bg-slate-800 rounded-[8px] shadow-lg shadow-slate-200/50 dark:shadow-slate-900/50 border border-slate-100 dark:border-slate-700 p-3">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
            <SecurityIcon className="text-primary" sx={{ fontSize: 16 }} />
          </div>
          <div>
            <h2 className="text-sm font-bold text-slate-800 dark:text-slate-100">{ID ? "Edit Role" : "Add Role"}</h2>
            <p className="text-xs text-slate-500 dark:text-slate-400">Configure role permissions</p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-3">
        {/* Role Name */}
        <div className="bg-white dark:bg-slate-800 rounded-[8px] shadow-lg shadow-slate-200/50 dark:shadow-slate-900/50 border border-slate-100 dark:border-slate-700 p-3">
          <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1.5">Role Name</label>
          <input
            name="roleName"
            value={formData.roleName}
            onChange={handleChange}
            type="text"
            className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all text-xs text-slate-800 dark:text-slate-200 placeholder:text-slate-400 dark:placeholder:text-slate-500"
            placeholder="Enter role name"
          />
        </div>

        {/* Route Access */}
        <div className="bg-white rounded-[8px] shadow-lg shadow-slate-200/50 border border-slate-100 overflow-hidden">
          <div className="px-3 py-2 border-b border-slate-200 dark:border-slate-600 flex items-center gap-2">
            <RouteIcon className="text-emerald-600" sx={{ fontSize: 16 }} />
            <h3 className="text-sm font-bold text-slate-800 dark:text-slate-100">Route Access</h3>
          </div>
          <div className="p-3 space-y-1.5 max-h-[400px] overflow-y-auto">
            {route?.map((item: any, i: number) => {
              const isChecked = formData.routePermission.includes(item.KEY);
              const itemWithChecked = { ...item, checked: isChecked };
              return (
                <Permission
                  handleChange={handleChange}
                  name={"routePermission"}
                  key={i}
                  indent={countDigits(item?.KEY) - 1}
                  item={itemWithChecked}
                  disabled={i === 0}
                />
              );
            })}
          </div>
        </div>

        {/* Permission Denied */}
        <div className="bg-white rounded-[8px] shadow-lg shadow-slate-200/50 border border-slate-100 overflow-hidden">
          <div className="px-3 py-2 border-b border-slate-200 dark:border-slate-600 flex items-center gap-2">
            <BlockIcon className="text-red-600" sx={{ fontSize: 16 }} />
            <h3 className="text-sm font-bold text-slate-800 dark:text-slate-100">Permission Denied</h3>
          </div>
          <div className="p-3 space-y-1.5">
            {actions?.map((item: any, i: number) => {
              const isChecked = formData.actionPermission.includes(item.KEY);
              const itemWithChecked = { ...item, checked: isChecked };
              return (
                <Permission
                  handleChange={handleChange}
                  name={"actionPermission"}
                  key={i}
                  item={itemWithChecked}
                />
              );
            })}
          </div>
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          className="w-full px-6 py-3 rounded-lg bg-gradient-to-r from-primary to-primaryLight text-white font-semibold hover:shadow-lg hover:shadow-primary/20 transition-all text-xs"
        >
          {ID ? "Update Role" : "Save Role"}
        </button>
      </form>
    </div>
  );
}

export default AddRole;
